import { Navigate, useLocation } from 'react-router-dom';

import { AuthStatus } from '@app/auth/api/auth.type';
import { Routes } from '@app/constants/routes';
import { AUTH_QUERY_PARAM_REDIRECT } from '@app/modules/auth/auth.constants';
import { useRedirectQueryParam } from '@app/modules/auth/hooks/useRedirectQueryParam';
import { isSafeRedirectPath } from '@app/router/utils/redirectPath.utils';
import { TypedNavigate } from '@app/router';
import { useStore } from '@app/store/useStore.hook';

/**
 * The catch-all for any URL none of RootRoutes' children matched. useFiltredRoutes removes
 * routes based on authStatus/role, it doesn't redirect - so the browser is still sitting on
 * the originally-requested URL whenever a guard filters away the route that used to match it,
 * and this is where that shows up. Three real cases:
 *
 * - An unauthenticated visitor filtered off a `/c/*` route: send them to sign in, carrying
 *   the path they were trying to reach.
 * - A signed-in visitor filtered off an `/auth/*` route (AuthRoutes is gated to
 *   UNAUTHORIZED) while that URL still carries a valid `redirect` target: go there directly
 *   instead of the dashboard default - see the race this closes, below.
 * - Anything else (a route the app genuinely doesn't have, or a signed-in visitor with the
 *   wrong role for where they landed): `/`, unchanged, since neither case is fixed by
 *   sending them to sign in again.
 */
export const RootRouteFallback: React.FC = () => {
  const location = useLocation();
  const authStatus = useStore.useAuthStatus();
  const { redirectTarget } = useRedirectQueryParam();

  const attemptedPath = location.pathname + location.search;
  const isUnauthenticatedCustomerRoute =
    authStatus === AuthStatus.UNAUTHORIZED && location.pathname.startsWith(Routes.customer.index);

  if (isUnauthenticatedCustomerRoute && isSafeRedirectPath(attemptedPath)) {
    return (
      <TypedNavigate
        to={Routes.auth.sign_in}
        params={{}}
        queryParams={{ [AUTH_QUERY_PARAM_REDIRECT]: attemptedPath }}
      />
    );
  }

  // Terugkeer-na-inloggen race, found and fixed 2026-08-22 (see NOTES.md for the full
  // mechanism): useFiltredRoutes' authStatus-gated route list recomputes the instant
  // authStatus flips to SIGNED_IN, which can land here - matching nothing under /auth/*
  // anymore - for one render pass before react-router has processed the login page's own
  // navigate(redirectTarget) call. Rather than racing to win that render, both paths now
  // agree on the same destination: a signed-in visitor caught here on an auth route with a
  // still-valid redirect target goes there directly, same validation as everywhere else
  // (useRedirectQueryParam - null unless isSafeRedirectPath already approved it), replace so
  // this detour never leaves a dead entry in history. No valid target: unchanged behaviour,
  // falls through to the default below (RootModule's own signed-in-goes-to-dashboard guard).
  const isAuthenticatedAuthRoute =
    authStatus === AuthStatus.SIGNED_IN && location.pathname.startsWith(Routes.auth.index);

  if (isAuthenticatedAuthRoute && redirectTarget) {
    return <Navigate to={redirectTarget} replace />;
  }

  return <TypedNavigate to={Routes.index} params={{}} />;
};
